import { ApplicationRef, EmbeddedViewRef, Injectable, NgZone, TemplateRef, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import type { Direction, GapSnap, Seg as BaseSeg } from '../marquee.types';
import { MarqueeDomService } from './marquee-dom.service';
import { MarqueeTickerService } from './marquee-ticker.service';

type Seg = BaseSeg & { view?: EmbeddedViewRef<unknown> | null };

@Injectable()
export class MarqueeEngineService {
  speed = 80;
  direction: Direction = 'left';
  pauseOnHover = true;
  isPlaying = true;
  gap = 32;
  autoFill = false;
  updateVisible = false;
  gapSnap: GapSnap = 'smooth';

  angularMode = true;

  tplRef?: TemplateRef<unknown>;
  appRef?: ApplicationRef;

  wrapperEl!: HTMLElement;
  aEl!: HTMLElement;
  bEl!: HTMLElement;
  poolEl!: HTMLElement;
  sourceEl!: HTMLElement;

  isPaused = false;
  private unsubTicker?: () => void;
  private lastWrapperW = -1;

  private A!: Seg;
  private B!: Seg;
  private touchedEdge = false;
  private releasedNext = false;

  private segs: Seg[] = [];
  private N = 0;

  private readonly PARK = 2000; // парковка вне экрана
  private htmlVersion = 0;
  private ro?: ResizeObserver;
  private mo?: MutationObserver;

  private resizeTimer: number | null = null;
  private isResizing = false;
  private resizeLock = 0;
  private wasRunningBeforeResize = false;
  private onWindowResize = () => this.scheduleResizeRecalc();

  private zone = inject(NgZone);
  private dom = inject(MarqueeDomService);
  private ticker = inject(MarqueeTickerService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  init(): void {
    if (!this.isBrowser) return;

    this.zone.runOutsideAngular(() => {
      this.fillSegmentContent({ el: this.aEl, x: 0, w: 1, hidden: false, ver: this.htmlVersion } as Seg);
      this.fillSegmentContent({ el: this.bEl, x: 0, w: 1, hidden: false, ver: this.htmlVersion } as Seg);

      this.measureAndSetHeight();
      if (this.autoFill) this.setupClassicInitial(false);
      else this.setupSnakeInitial();

      this.lastWrapperW = Math.round(this.wrapperEl.clientWidth);

      this.startTicker();
      this.initObservers();
      window.addEventListener('resize', this.onWindowResize);
    });
  }

  destroy(): void {
    this.stopTicker();
    this.ro?.disconnect();
    this.mo?.disconnect();
    this.ro?.disconnect();
    this.mo?.disconnect();
    if (this.isBrowser) {
      window.removeEventListener('resize', this.onWindowResize);
    }
    if (this.resizeTimer != null) window.clearTimeout(this.resizeTimer);

    // зачистка живых вьюх
    if (this.angularMode && this.appRef) {
      const kill = (seg?: Seg) => {
        if (seg?.view) this.dom.unmountView(seg.view, this.appRef!);
      };
      kill(this.A);
      kill(this.B);
      for (const s of this.segs) kill(s);
    }
  }

  pause(): void {
    if (this.isPlaying) this.isPaused = true;
  }
  resume(): void {
    if (this.isPlaying) this.isPaused = false;
  }

  private startTicker() {
    this.stopTicker();
    this.unsubTicker = this.ticker.subscribe(dt => {
      if (!this.isPlaying || this.isPaused || dt <= 0 || dt >= 0.5) return;
      if (this.autoFill) this.tickClassic(dt);
      else this.tickSnake(dt);
    });
  }
  private stopTicker() {
    this.unsubTicker?.();
    this.unsubTicker = undefined;
  }

  private dirSign(): number {
    return this.direction === 'left' ? -1 : 1;
  }

  private measureAndSetHeight() {
    const ch = this.dom.measureHeight(this.sourceEl);
    this.wrapperEl.style.height = `${Math.ceil(ch)}px`;
  }

  private setHidden(seg: Seg, hidden: boolean) {
    seg.hidden = hidden;
    this.dom.setHidden(seg.el, hidden);
  }
  private applyTransform(seg: Seg) {
    if (!seg.hidden) this.dom.applyTranslateX(seg.el, seg.x);
  }

  private fillSegmentContent(seg: Seg) {
    if (this.angularMode && this.tplRef && this.appRef) {
      if (seg.view) this.dom.unmountView(seg.view, this.appRef);
      seg.view = this.dom.mountTemplateInto(seg.el, this.tplRef, this.appRef);
    } else {
      this.dom.cloneInto(this.sourceEl, seg.el);
      if (seg.view && this.appRef) {
        this.dom.unmountView(seg.view, this.appRef);
        seg.view = null;
      }
    }
  }

  private setupSnakeInitial() {
    const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
    const wW = this.wrapperEl.clientWidth;
    const ver = this.htmlVersion;

    this.A = { el: this.aEl, x: 0, w: cw, hidden: false, ver, view: null };
    this.B = { el: this.bEl, x: wW + this.PARK, w: cw, hidden: true, ver, view: null };

    this.setHidden(this.A, false);
    this.setHidden(this.B, true);

    this.fillSegmentContent(this.A);
    this.fillSegmentContent(this.B);

    this.applyTransform(this.A);
    this.touchedEdge = false;
    this.releasedNext = false;

    while (this.poolEl.firstChild) this.poolEl.removeChild(this.poolEl.firstChild);
    this.segs = [];
    this.N = 0;
  }

  private tickSnake(dt: number) {
    const v = Math.max(1, Number(this.speed) || 1);
    const dx = this.dirSign() * v * dt;

    this.A.x += dx;
    this.B.x += dx;

    const wW = this.wrapperEl.clientWidth;
    const g = Math.max(0, this.gap);

    const leftMost = this.A.x <= this.B.x ? this.A : this.B;
    const rightMost = leftMost === this.A ? this.B : this.A;
    const head = this.direction === 'left' ? leftMost : rightMost;
    const next = head === this.A ? this.B : this.A;

    if (!this.touchedEdge) {
      if (this.direction === 'left') {
        if (head.x <= 0) this.touchedEdge = true;
      } else {
        if (head.x + head.w >= wW) this.touchedEdge = true;
      }
    }

    if (this.touchedEdge && !this.releasedNext) {
      if (this.direction === 'left') {
        const freeRight = wW - (head.x + head.w);
        if (freeRight > g) {
          if (next.ver !== this.htmlVersion) {
            const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
            this.fillSegmentContent(next);
            next.w = cw;
            next.ver = this.htmlVersion;
          }
          next.x = wW;
          this.setHidden(next, false);
          this.applyTransform(next);
          this.releasedNext = true;
        }
      } else {
        const freeLeft = head.x;
        if (freeLeft > g) {
          if (next.ver !== this.htmlVersion) {
            const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
            this.fillSegmentContent(next);
            next.w = cw;
            next.ver = this.htmlVersion;
          }
          next.x = -next.w;
          this.setHidden(next, false);
          this.applyTransform(next);
          this.releasedNext = true;
        }
      }
    }

    if (this.releasedNext) {
      const left = this.A.x <= this.B.x ? this.A : this.B;
      const right = left === this.A ? this.B : this.A;
      const target = left.x + left.w + g;

      if (right.x < target) {
        if (this.gapSnap === 'instant') right.x = target;
        else {
          const maxCatch = Math.abs(v * dt) * 2;
          right.x = Math.min(target, right.x + maxCatch);
        }
      }
    }

    if (this.direction === 'left') {
      if (head.x + head.w < 0) {
        if (head.ver !== this.htmlVersion) {
          const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
          this.fillSegmentContent(head);
          head.w = cw;
          head.ver = this.htmlVersion;
        }
        head.x = wW + this.PARK;
        this.setHidden(head, true);
        this.touchedEdge = false;
        this.releasedNext = false;
      }
    } else {
      if (head.x > wW) {
        if (head.ver !== this.htmlVersion) {
          const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
          this.fillSegmentContent(head);
          head.w = cw;
          head.ver = this.htmlVersion;
        }
        head.x = -this.PARK - head.w;
        this.setHidden(head, true);
        this.touchedEdge = false;
        this.releasedNext = false;
      }
    }

    this.applyTransform(this.A);
    this.applyTransform(this.B);
  }

  private setupClassicInitial(preservePhase: boolean) {
    // очистка пула
    while (this.poolEl.firstChild) this.poolEl.removeChild(this.poolEl.firstChild);

    const cwNew = Math.max(this.dom.measureWidth(this.sourceEl), 1);
    const wW = this.wrapperEl.clientWidth;
    const g = Math.max(0, this.gap);
    const Lnew = cwNew + g;

    let phase = 0;
    if (preservePhase && this.segs.length) {
      const cwOld = this.segs[0].w;
      const Lold = cwOld + g;

      if (this.direction === 'left') {
        const leftMost = this.segs.reduce((a, b) => (a.x < b.x ? a : b));
        const raw = -leftMost.x;
        phase = ((raw % Lold) + Lold) % Lold;
      } else {
        const rightMost = this.segs.reduce((a, b) => (a.x + a.w > b.x + b.w ? a : b));
        const raw = wW - (rightMost.x + rightMost.w);
        phase = ((raw % Lold) + Lold) % Lold;
      }
      const ratio = Lold > 0 ? phase / Lold : 0;
      phase = ratio * Lnew;
    }

    this.N = Math.max(2, Math.ceil((wW + Lnew) / Lnew));
    this.segs = [];
    const ver = this.htmlVersion;

    let x0: number;
    if (this.direction === 'left') x0 = -phase;
    else {
      const rightEdge = wW - phase;
      x0 = rightEdge - cwNew;
    }

    const A: Seg = { el: this.aEl, x: x0, w: cwNew, hidden: false, ver, view: null };
    this.fillSegmentContent(A);
    this.segs.push(A);

    const B: Seg = { el: this.bEl, x: 0, w: cwNew, hidden: false, ver, view: null };
    this.fillSegmentContent(B);
    this.segs.push(B);

    if (this.direction === 'left') {
      this.segs[1].x = this.segs[0].x + this.segs[0].w + g;
      for (let i = 2; i < this.N; i++) {
        const node = this.dom.createCloneFrom(this.sourceEl, 'marquee-item');
        this.poolEl.appendChild(node);
        const prev = this.segs[i - 1];
        const seg: Seg = { el: node, x: prev.x + prev.w + g, w: cwNew, hidden: false, ver, view: null };
        this.fillSegmentContent(seg);
        this.segs.push(seg);
      }
    } else {
      this.segs[1].x = this.segs[0].x - (cwNew + g);
      for (let i = 2; i < this.N; i++) {
        const node = this.dom.createCloneFrom(this.sourceEl, 'marquee-item');
        this.poolEl.appendChild(node);
        const prev = this.segs[i - 1];
        const seg: Seg = { el: node, x: prev.x - (cwNew + g), w: cwNew, hidden: false, ver, view: null };
        this.fillSegmentContent(seg);
        this.segs.push(seg);
      }
      this.segs.sort((a, b) => a.x - b.x);
    }

    for (const s of this.segs) {
      this.setHidden(s, false);
      this.applyTransform(s);
    }
  }

  private tickClassic(dt: number) {
    const v = Math.max(1, Number(this.speed) || 1);
    const dx = this.dirSign() * v * dt;

    const wW = this.wrapperEl.clientWidth;
    const g = Math.max(0, this.gap);
    const overscan = 64;

    for (const s of this.segs) s.x += dx;

    if (this.direction === 'left') {
      let rightMostEnd = Math.max(...this.segs.map(s => s.x + s.w));
      for (const s of this.segs) {
        if (s.x + s.w < -overscan) {
          if (s.ver !== this.htmlVersion) {
            const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
            this.fillSegmentContent(s);
            s.w = cw;
            s.ver = this.htmlVersion;
          }
          s.x = rightMostEnd + g;
          rightMostEnd = s.x + s.w;
        }
      }
      this.segs.sort((a, b) => a.x - b.x);
      const maxCatch = Math.abs(v * dt) * 2;
      for (let i = 1; i < this.segs.length; i++) {
        const prev = this.segs[i - 1],
          cur = this.segs[i];
        const target = prev.x + prev.w + g;
        if (cur.x < target) {
          cur.x = this.gapSnap === 'instant' ? target : Math.min(target, cur.x + maxCatch);
        }
      }
    } else {
      let leftMost = Math.min(...this.segs.map(s => s.x));
      for (let i = this.segs.length - 1; i >= 0; i--) {
        const s = this.segs[i];
        if (s.x > wW + overscan) {
          if (s.ver !== this.htmlVersion) {
            const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
            this.fillSegmentContent(s);
            s.w = cw;
            s.ver = this.htmlVersion;
          }
          s.x = leftMost - (s.w + g);
          leftMost = s.x;
        }
      }
      this.segs.sort((a, b) => a.x - b.x);
      const maxCatch = Math.abs(v * dt) * 2;
      for (let i = 0; i < this.segs.length - 1; i++) {
        const cur = this.segs[i],
          next = this.segs[i + 1];
        const target = next.x - (cur.w + g);
        if (cur.x > target) {
          cur.x = this.gapSnap === 'instant' ? target : Math.max(target, cur.x - maxCatch);
        }
      }
    }

    // мягкий culling (visibility)
    const Lb = -overscan,
      Rb = wW + overscan;
    for (const s of this.segs) {
      const off = s.x + s.w < Lb || s.x > Rb;
      if (off && !s.hidden) this.setHidden(s, true);
      if (!off && s.hidden) {
        this.setHidden(s, false);
        this.applyTransform(s);
      }
    }

    for (const s of this.segs) this.applyTransform(s);
  }

  private rebuildAfterResize() {
    this.resizeLock++;
    try {
      this.measureAndSetHeight();
      if (this.autoFill) this.setupClassicInitial(true /* preserve phase */);
      else this.setupSnakeInitial();
      this.lastWrapperW = Math.round(this.wrapperEl.clientWidth);
    } finally {
      this.resizeLock--;
    }
  }

  private scheduleResizeRecalc() {
    if (this.resizeLock > 0) return;
    const curW = Math.round(this.wrapperEl.clientWidth);
    if (Math.abs(curW - this.lastWrapperW) <= 1) return;

    if (!this.isResizing) {
      this.isResizing = true;
      this.wasRunningBeforeResize = !this.isPaused && this.isPlaying;
      if (this.wasRunningBeforeResize) this.pause();
    }
    if (this.resizeTimer != null) window.clearTimeout(this.resizeTimer);

    this.resizeTimer = window.setTimeout(() => {
      this.resizeTimer = null;
      this.rebuildAfterResize();
      this.isResizing = false;
      if (this.wasRunningBeforeResize) this.resume();
    }, 180);
  }

  private initObservers(): void {
    this.ro = new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!this.ro) return;
        if (this.resizeLock > 0) return;

        for (const e of entries) {
          const t = e.target as HTMLElement;
          if (t === this.wrapperEl) {
            const curW = Math.round(this.wrapperEl.clientWidth);
            if (Math.abs(curW - this.lastWrapperW) > 1) this.scheduleResizeRecalc();
          } else if (t === this.sourceEl) {
            this.handleContentMutate();
          }
        }
      });
    });
    this.ro.observe(this.wrapperEl);
    this.ro.observe(this.sourceEl);

    try {
      this.mo = new MutationObserver(() => {
        if (this.resizeLock > 0) return;
        this.handleContentMutate();
      });
      this.mo.observe(this.sourceEl, { childList: true, subtree: true, characterData: true });
    } catch { }
  }

  private handleContentMutate(): void {
    if (this.resizeLock > 0) return;
    this.htmlVersion++;
    this.measureAndSetHeight();

    const cw = Math.max(this.dom.measureWidth(this.sourceEl), 1);
    if (this.updateVisible) this.updateAllSegments(cw);
    else this.updateHiddenSegments(cw);
  }

  private updateAllSegments(cw: number): void {
    if (this.autoFill) {
      for (const s of this.segs) {
        this.fillSegmentContent(s);
        s.w = cw;
        s.ver = this.htmlVersion;
      }
    } else {
      if (this.A) {
        this.fillSegmentContent(this.A);
        this.A.w = cw;
        this.A.ver = this.htmlVersion;
      }
      if (this.B) {
        this.fillSegmentContent(this.B);
        this.B.w = cw;
        this.B.ver = this.htmlVersion;
      }
    }
  }

  private updateHiddenSegments(cw: number): void {
    if (this.autoFill) {
      const wW = this.wrapperEl.clientWidth;
      const overscan = 64;
      const Lb = -overscan,
        Rb = wW + overscan;
      for (const s of this.segs) {
        const off = s.x + s.w < Lb || s.x > Rb;
        if (off && s.ver !== this.htmlVersion) {
          this.fillSegmentContent(s);
          s.w = cw;
          s.ver = this.htmlVersion;
        }
      }
    } else {
      if (this.A && this.A.hidden && this.A.ver !== this.htmlVersion) {
        this.fillSegmentContent(this.A);
        this.A.w = cw;
        this.A.ver = this.htmlVersion;
      }
      if (this.B && this.B.hidden && this.B.ver !== this.htmlVersion) {
        this.fillSegmentContent(this.B);
        this.B.w = cw;
        this.B.ver = this.htmlVersion;
      }
    }
  }
}
